<script setup lang="ts">
import SrDataTable from '@/components/tables/SrDataTable.vue'
import type { TableColumn } from '@/components/alerts/types'
import type { GmgnPriceResponse } from '@/services/dex-pools-v2-service/types'
import { noExponents } from '@/utils'
import { i18n } from '@/i18n'
import { computed } from 'vue'

const props = defineProps<{
  trades: GmgnPriceResponse[]
  rounding?: number
}>()

const { t } = i18n.global

const sortedTrades = computed(() => {
  return [...props.trades].sort((a, b) => b.ts - a.ts)
})

const columns: TableColumn[] = [
  {
    field: 'ts',
    header: 'table.time',
    width: '170px',
    class: 'text-center',
    inner: 'span',
    innerProps: {
      'class': 'pit'
    }
  },
  {
    field: 'price',
    header: 'table.price',
    width: '140px',
    class: 'text-center',
    inner: 'span',
    innerProps: {
      'class': 'pit'
    }
  },
  {
    field: 'quote_amount',
    header: 'table.volume',
    width: '120px',
    class: 'text-center',
    inner: 'span',
    innerProps: {
      'class': 'pit'
    }
  },
  {
    field: 'side',
    header: 'side',
    width: '100px',
    class: "text-center",
    inner: 'span',
    dynamic: true,
    innerPropsGenerator: (v: object) => {
      const data = v as GmgnPriceResponse
      const myClass = data.side == 'buy' ? 'success' : 'error'
      return {
        class: `pit ${myClass}`,
      }
    }
  },
]

const formattingRules = {
  ts: (v: number) => {
    const ms = v < 1e12 ? v * 1000 : v
    return new Date(ms).toLocaleString('ru-RU')
  },
  price: (v: number) => {
    if (v === null || v === undefined) return '—'
    const fixed = props.rounding ?? 8
    const formatted = noExponents(Number(v.toFixed(fixed)))
    return formatted ?? '—'
  },
  quote_amount: (v: number | null) => {
    if (v === null || v === undefined) return '—'
    return `$${Number(v).toFixed(2)}`
  },
  side: (v: string | undefined) => {
    if (!v) return '—'
    return t(v as 'buy' | 'sell')
  },
  pool: (v: string) => (v ? v : '—'),
}
</script>

<template>
  <SrDataTable size="small" :value="sortedTrades" :columns="columns" :formatting-rules="formattingRules">
    <template #empty>
      <div class="p-0 text-center text-sm text-muted">{{ $t('emptyMessage') }}</div>
    </template>
  </SrDataTable>
</template>
